import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Card,
    Typography,
    TextField,
    Button,
    Grid,
    Autocomplete,
    CircularProgress,
    MenuItem,
    Fade,
    ThemeProvider,
    createTheme,
    IconButton
} from '@mui/material';
import { Store, LocalOffer, CardGiftcard, Save, Refresh, Search, ArrowRight, Assessment, CropFree } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useForm, Controller } from 'react-hook-form';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import QRCode from 'qrcode';
import Header from '../Header';
import { local_signage, local_url } from '../Const';

const theme = createTheme({
    palette: {
        primary: {
            main: '#1e88e5',
            light: '#6ab7ff',
            dark: '#005cb2',
        },
        secondary: {
            main: '#28a745',
        },
        background: {
            default: '#f4f7fb',
            paper: '#ffffff',
        },
        text: {
            primary: '#2d3748',
            secondary: '#718096',
        }
    },
    typography: {
        fontFamily: '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        button: {
            textTransform: 'none',
            fontWeight: 600,
        },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'small',
            },
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#fafbfc',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            backgroundColor: '#ffffff',
                        },
                        '&.Mui-focused': {
                            backgroundColor: '#ffffff',
                            boxShadow: '0 0 0 3px rgba(30, 136, 229, 0.1)',
                        }
                    },
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    padding: '8px 24px',
                    transition: 'all 0.2s ease-in-out',
                },
                containedPrimary: {
                    boxShadow: '0 4px 14px 0 rgba(30, 136, 229, 0.39)',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 20px rgba(30, 136, 229, 0.4)',
                    }
                },
                outlined: {
                    borderWidth: '2px',
                    '&:hover': {
                        borderWidth: '2px',
                    }
                }
            }
        }
    }
});



export const AdminQRCoupon = () => {
    const [currentTab, setCurrentTab] = useState(0);

    return (
        <>
            <Header />
            <ThemeProvider theme={theme}>
                <Box sx={{
                    p: { xs: 2, md: 5 },
                    minHeight: '100vh',
                    backgroundColor: 'background.default',
                    display: 'flex',
                    justifyContent: 'center'
                }}>
                    <ToastContainer position="top-right" autoClose={3000} theme="colored" hideProgressBar />

                    <Box sx={{ width: '100%', maxWidth: 1100 }}>
                        {/* Header Section */}
                        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>


                            {/* Pill Navigation */}
                            <Box sx={{
                                display: 'flex',
                                backgroundColor: '#e2e8f0',
                                borderRadius: '50px',
                                p: 0.5,
                                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
                            }}>
                                {[
                                    { label: 'Create Partner', icon: <Store fontSize="small" /> },
                                    { label: 'Promotion QR', icon: <LocalOffer fontSize="small" /> },
                                    { label: 'Promotion Report', icon: <Assessment fontSize="small" /> },
                                    { label: 'List Partner QR', icon: <CardGiftcard fontSize="small" /> },
                                    { label: 'Scan Report', icon: <CropFree fontSize="small" /> },
                                ].map((tab, idx) => (
                                    <Button
                                        key={idx}
                                        disableElevation
                                        onClick={() => setCurrentTab(idx)}
                                        startIcon={tab.icon}
                                        sx={{
                                            borderRadius: '50px',
                                            px: 3,
                                            py: 1,
                                            color: currentTab === idx ? '#fff' : 'text.secondary',
                                            backgroundColor: currentTab === idx ? 'primary.main' : 'transparent',
                                            fontWeight: currentTab === idx ? 600 : 500,
                                            '&:hover': {
                                                backgroundColor: currentTab === idx ? 'primary.main' : 'rgba(255,255,255,0.5)',
                                            }
                                        }}
                                    >
                                        {tab.label}
                                    </Button>
                                ))}
                            </Box>
                        </Box>

                        {/* Tab Content Area */}
                        <Box sx={{ position: 'relative' }}>
                            <Fade in={currentTab === 0} mountOnEnter unmountOnExit timeout={400}>
                                <Box><CreatePartnerForm /></Box>
                            </Fade>
                            <Fade in={currentTab === 1} mountOnEnter unmountOnExit timeout={400}>
                                <Box><PromotionForm /></Box>
                            </Fade>
                            <Fade in={currentTab === 2} mountOnEnter unmountOnExit timeout={400}>
                                <Box><PromotionDetailReport /></Box>
                            </Fade>
                            <Fade in={currentTab === 3} mountOnEnter unmountOnExit timeout={400}>
                                <Box><PartnerQRListTab /></Box>
                            </Fade>
                            <Fade in={currentTab === 4} mountOnEnter unmountOnExit timeout={400}>
                                <Box><ScanReport /></Box>
                            </Fade>
                            <Fade in={currentTab === 5} mountOnEnter unmountOnExit timeout={400}>
                                <Box><PlaceholderTab title="Promotional Schemes" description="Define grand schemes, multi-tier rewards, and logic." icon={<CardGiftcard sx={{ fontSize: 80, color: '#e2e8f0' }} />} /></Box>
                            </Fade>
                        </Box>
                    </Box>
                </Box>
            </ThemeProvider>
        </>
    );
};

const CreatePartnerForm = () => {
    const { handleSubmit, control, reset, watch, formState: { errors } } = useForm({
        defaultValues: {
            PartnerName: '',
            Address: '',
            Area: null,
            Place: null,
            PinCode: '',
            BusinessType: null,
            ContactPerson: '',
            ContactMobile: '',
            Promotion: null
        }
    });

    const [loading, setLoading] = useState(false);
    const [businessTypes, setBusinessTypes] = useState([]);
    const [businessTypesLoading, setBusinessTypesLoading] = useState(false);
    const [areaOptions, setAreaOptions] = useState([]);
    const [areaLoading, setAreaLoading] = useState(false);
    const [placeOptions, setPlaceOptions] = useState([]);
    const [placeLoading, setPlaceLoading] = useState(false);
    const [promotions, setPromotions] = useState([]);
    const [promotionsLoading, setPromotionsLoading] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [qrPartnerName, setQrPartnerName] = useState('');
    const [dupNameWarning, setDupNameWarning] = useState('');
    const [dupMobileWarning, setDupMobileWarning] = useState('');
    const [dupChecking, setDupChecking] = useState(false);
    const debounceTimer = useRef(null);

    useEffect(() => {
        const fetchBusinessTypes = async () => {
            setBusinessTypesLoading(true);
            try {
                const token = localStorage.getItem('AuthorizationToken');
                const res = await axios.get(`${local_signage}/api/v1/fetchbusinesstype`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.data?.success && Array.isArray(res.data.data)) {
                    setBusinessTypes(res.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch business types:', err);
            } finally {
                setBusinessTypesLoading(false);
            }
        };
        fetchBusinessTypes();
    }, []);

    useEffect(() => {
        const fetchAreas = async () => {
            setAreaLoading(true);
            try {
                const token = localStorage.getItem('AuthorizationToken');
                const res = await axios.get(`${local_url}/api/SFArea/0010003871`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (Array.isArray(res.data)) {
                    setAreaOptions(res.data.map(a => ({ id: a.sfAreaId, label: a.sfAreaName })));
                }
            } catch (err) {
                console.error('Failed to fetch areas:', err);
            } finally {
                setAreaLoading(false);
            }
        };
        fetchAreas();
    }, []);

    useEffect(() => {
        const fetchPromotions = async () => {
            setPromotionsLoading(true);
            try {
                const token = localStorage.getItem('AuthorizationToken');
                const res = await axios.get(`${local_signage}/api/v1/fetchpromotion`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.data?.success && Array.isArray(res.data.data)) {
                    setPromotions(res.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch promotions:', err);
            } finally {
                setPromotionsLoading(false);
            }
        };
        fetchPromotions();
    }, []);

    const watchedPinCode = watch('PinCode');
    const watchedPartnerName = watch('PartnerName');
    const watchedContactMobile = watch('ContactMobile');
    const watchedPromotion = watch('Promotion');

    useEffect(() => {
        setDupNameWarning('');
        setDupMobileWarning('');
        if (!watchedPartnerName && !watchedContactMobile) return;

        // Use selected promotion or fall back to the first loaded promotion
        const promoId = watchedPromotion?.id || (promotions.length > 0 ? promotions[0].PromotionId : null);
        if (!promoId) return;

        clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(async () => {
            setDupChecking(true);
            try {
                const token = localStorage.getItem('AuthorizationToken');
                const res = await axios.get(
                    `${local_signage}/api/v1/fetchpromotionpartnerlist?PromotionID=${promoId}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                const list = Array.isArray(res.data) ? res.data : [];

                if (watchedPartnerName) {
                    const nameDup = list.find(p =>
                        p.PartnerName.trim().toLowerCase() === watchedPartnerName.trim().toLowerCase()
                    );
                    setDupNameWarning(nameDup
                        ? `Duplicate: "${nameDup.PartnerName}" already exists (ID: ${nameDup.PartnerId})`
                        : ''
                    );
                }

                if (watchedContactMobile && watchedContactMobile.length === 10) {
                    const mobileDup = list.find(p =>
                        p.ContactMobile && p.ContactMobile === watchedContactMobile
                    );
                    setDupMobileWarning(mobileDup
                        ? `Mobile already used by "${mobileDup.PartnerName}" (ID: ${mobileDup.PartnerId})`
                        : ''
                    );
                } else {
                    setDupMobileWarning('');
                }
            } catch (err) {
                console.error('Duplicate check failed:', err);
            } finally {
                setDupChecking(false);
            }
        }, 1500);

        return () => clearTimeout(debounceTimer.current);
    }, [watchedPartnerName, watchedContactMobile, watchedPromotion]);

    useEffect(() => {
        const pincode = watchedPinCode;
        if (!pincode || String(pincode).length !== 6) {
            setPlaceOptions([]);
            return;
        }
        const fetchPlaces = async () => {
            setPlaceLoading(true);
            try {
                const token = localStorage.getItem('AuthorizationToken');
                const res = await axios.get(`${local_url}/api/Place/${pincode}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.data?.places && Array.isArray(res.data.places)) {
                    setPlaceOptions(res.data.places.map(p => ({ id: p.placeId, label: p.placeName })));
                } else {
                    setPlaceOptions([]);
                }
            } catch (err) {
                console.error('Failed to fetch places:', err);
                setPlaceOptions([]);
            } finally {
                setPlaceLoading(false);
            }
        };
        fetchPlaces();
    }, [watchedPinCode]);

    const onSubmit = async (data) => {
        if (dupNameWarning || dupMobileWarning) {
            toast.error('Please resolve duplicate warnings before registering.');
            return;
        }

        setLoading(true);
        setQrDataUrl(null);
        setQrPartnerName('');

        try {
            const token = localStorage.getItem('AuthorizationToken');
            const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

            // Step 1: Create partner
            const partnerRes = await axios.post(`${local_signage}/api/v1/upsertpromopartner`, {
                PartnerId: "",
                PartnerName: data.PartnerName,
                Address: data.Address,
                PlaceId: data.Place?.id || "",
                PinCode: data.PinCode,
                AreaId: data.Area?.id || "",
                BusinessTypeCode: data.BusinessType?.code || "",
                ContactPerson: data.ContactPerson || "",
                ContactMobile: data.ContactMobile || ""
            }, { headers });

            const partnerId = partnerRes.data?.data?.PartnerId;
            if (!partnerId) {
                toast.error('Partner created but no PartnerId returned.');
                setLoading(false);
                return;
            }

            toast.success(`Partner registered! ID: ${partnerId}`);

            // Step 2: Link partner to promotion and generate QR
            if (true) {
                // Fetch existing link
                const promotionId = data.Promotion?.id || "";
                let detailRes = await axios.get(
                    `${local_signage}/api/v1/fetchpromotionpartnerdetail?PromotionID=${promotionId}&PartnerID=${partnerId}`,
                    { headers }
                );
                let detail = Array.isArray(detailRes.data) && detailRes.data.length > 0 ? detailRes.data[0] : null;

                // Insert link if not found
                if (!detail) {
                    await axios.post(
                        `${local_signage}/api/v1/insertpromotionpartnerdetail`,
                        { PromotionId: promotionId, PartnerId: partnerId },
                        { headers }
                    );
                    // Re-fetch after insert
                    detailRes = await axios.get(
                        `${local_signage}/api/v1/fetchpromotionpartnerdetail?PromotionID=${promotionId}&PartnerID=${partnerId}`,
                        { headers }
                    );
                    detail = Array.isArray(detailRes.data) && detailRes.data.length > 0 ? detailRes.data[0] : null;
                }

                if (!detail?.UniqueId) {
                    toast.error('Promotion linked but QR could not be generated — no UniqueId.');
                    reset();
                    setLoading(false);
                    return;
                }

                // Generate QR
                const qrUrl = `https://promotions.abispro.com/UID:${detail.UniqueId}`;
                const url = await QRCode.toDataURL(qrUrl, { width: 300, margin: 2 });
                setQrDataUrl(url);
                setQrPartnerName(data.PartnerName);
                toast.success('QR Code generated!');
            }

            reset();
        } catch (error) {
            console.error(error);
            toast.error('Failed to register partner. Please check details.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card sx={{
            borderRadius: '20px',
            boxShadow: '0 12px 40px -12px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.04)',
            overflow: 'visible'
        }}>
            <Box sx={{ p: 4 }}>


                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                        {/* Partner Name & Business Type */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="PartnerName"
                                control={control}
                                rules={{ required: 'Partner name is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Partner Name *"
                                        fullWidth
                                        placeholder="e.g. Acme Corporation"
                                        error={!!errors.PartnerName || !!dupNameWarning}
                                        helperText={errors.PartnerName?.message || (dupChecking && !dupNameWarning ? '⏳ Checking...' : dupNameWarning) || ''}
                                        InputProps={{
                                            endAdornment: dupChecking
                                                ? <CircularProgress size={14} color="warning" />
                                                : null
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="BusinessType"
                                control={control}
                                rules={{ required: 'Required' }}
                                render={({ field: { onChange, value } }) => (
                                    <Autocomplete
                                        options={businessTypes.map(bt => ({ code: bt.BusinessTypeCode, label: bt.BusinessTypeName }))}
                                        loading={businessTypesLoading}
                                        getOptionLabel={(option) => option.label || ""}
                                        isOptionEqualToValue={(option, val) => option.code === val?.code}
                                        onChange={(_, item) => onChange(item)}
                                        value={value}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Business Type *"
                                                error={!!errors.BusinessType}
                                                helperText={errors.BusinessType?.message}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {businessTypesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </React.Fragment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Address Block */}
                        <Grid item xs={12}>
                            <Controller
                                name="Address"
                                control={control}
                                rules={{ required: 'Address is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Full Registered Address *"
                                        fullWidth
                                        multiline
                                        rows={1}
                                        placeholder="123 Commerce St, Suite 400..."
                                        error={!!errors.Address}
                                        helperText={errors.Address?.message}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Geographic Context — Area → PinCode → Place */}
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="Area"
                                control={control}
                                rules={{ required: 'Required' }}
                                render={({ field: { onChange, value } }) => (
                                    <Autocomplete
                                        options={areaOptions}
                                        loading={areaLoading}
                                        getOptionLabel={(option) => option.label || ""}
                                        isOptionEqualToValue={(option, val) => option.id === val.id}
                                        onChange={(_, item) => onChange(item)}
                                        value={value}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Area Name *"
                                                error={!!errors.Area}
                                                helperText={errors.Area?.message}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {areaLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </React.Fragment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Controller
                                name="PinCode"
                                control={control}
                                rules={{
                                    required: 'Pincode required',
                                    pattern: { value: /^[0-9]{6}$/, message: 'Must be 6 digits' }
                                }}
                                render={({ field }) => (
                                    <TextField
                                        name={field.name}
                                        ref={field.ref}
                                        value={typeof field.value === 'string' ? field.value : ''}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                            field.onChange(val);
                                        }}
                                        onBlur={field.onBlur}
                                        label="Postal Code *"
                                        fullWidth
                                        placeholder="000000"
                                        error={!!errors.PinCode}
                                        helperText={errors.PinCode?.message}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Controller
                                name="Place"
                                control={control}
                                rules={{ required: 'Required' }}
                                render={({ field: { onChange, value } }) => (
                                    <Autocomplete
                                        options={placeOptions}
                                        loading={placeLoading}
                                        getOptionLabel={(option) => option.label || ""}
                                        isOptionEqualToValue={(option, val) => option.id === val.id}
                                        onChange={(_, item) => onChange(item)}
                                        value={value}
                                        noOptionsText={watchedPinCode?.length === 6 ? 'No places found' : 'Enter 6-digit pincode first'}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Place Name *"
                                                error={!!errors.Place}
                                                helperText={errors.Place?.message}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {placeLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </React.Fragment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Owner / Contact Details */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="ContactPerson"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Owner Name"
                                        fullWidth
                                        placeholder="e.g. John Doe"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="ContactMobile"
                                control={control}
                                rules={{
                                    pattern: { value: /^[0-9]{10}$/, message: 'Must be 10 digits' }
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Owner Mobile No"
                                        fullWidth
                                        placeholder="e.g. 9876543210"
                                        onChange={e => field.onChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                        error={!!errors.ContactMobile || !!dupMobileWarning}
                                        helperText={errors.ContactMobile?.message || dupMobileWarning || ''}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Promotion Scheme */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="Promotion"
                                control={control}
                                rules={{ required: 'Please select a promotion scheme' }}
                                render={({ field: { onChange, value } }) => (
                                    <Autocomplete
                                        options={promotions.map(p => ({ id: p.PromotionId, label: `${p.PromotionSchemeName} — ${p.PromotionName}` }))}
                                        loading={promotionsLoading}
                                        getOptionLabel={(option) => option.label || ""}
                                        isOptionEqualToValue={(option, val) => option.id === val?.id}
                                        onChange={(_, item) => onChange(item)}
                                        value={value}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Promotion Scheme *"
                                                error={!!errors.Promotion}
                                                helperText={errors.Promotion?.message}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {promotionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </React.Fragment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Submit Action */}
                        <Grid item xs={12}>
                            <Box sx={{
                                display: 'flex',
                                gap: 2,
                                justifyContent: 'flex-end',
                                pt: 2,
                                borderTop: '1px solid #f0f2f5',
                                mt: 1
                            }}>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={() => { reset(); setQrDataUrl(null); setQrPartnerName(''); setDupNameWarning(''); setDupMobileWarning(''); }}
                                    disabled={loading}
                                    sx={{ color: 'text.secondary', borderColor: '#e2e8f0' }}
                                >
                                    Clear Form
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowRight />}
                                    disabled={loading}
                                    sx={{ minWidth: 160 }}
                                >
                                    {loading ? 'Registering...' : 'Register Partner'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>

                {/* Generated QR Code */}
                {qrDataUrl && (
                    <Box sx={{
                        mt: 4,
                        pt: 4,
                        borderTop: '1px solid #f0f2f5',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                    }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            QR Code — {qrPartnerName}
                        </Typography>
                        <Box sx={{
                            p: 2,
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.06)'
                        }}>
                            <img src={qrDataUrl} alt="QR Code" style={{ display: 'block', width: 240, height: 240 }} />
                        </Box>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                const a = document.createElement('a');
                                a.href = qrDataUrl;
                                a.download = `QR_${qrPartnerName.replace(/\s+/g, '_')}.png`;
                                a.click();
                            }}
                        >
                            Download QR
                        </Button>
                    </Box>
                )}
            </Box>
        </Card>
    );
};

const PromotionForm = () => {
    const { handleSubmit, control, reset, watch, setValue, formState: { errors } } = useForm({
        defaultValues: {
            Promotion: null,
            Partner: null,
        }
    });

    const [promotions, setPromotions] = useState([]);
    const [promotionsLoading, setPromotionsLoading] = useState(false);
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnerDetail, setPartnerDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState(null);

    const selectedPromotion = watch('Promotion');
    const selectedPartner = watch('Partner');

    useEffect(() => {
        if (!selectedPromotion?.id || !selectedPartner?.id) {
            setPartnerDetail(null);
            return;
        }
        const fetchDetail = async () => {
            setDetailLoading(true);
            try {
                const token = localStorage.getItem('AuthorizationToken');
                const res = await axios.get(
                    `${local_signage}/api/v1/fetchpromotionpartnerdetail?PromotionID=${selectedPromotion.id}&PartnerID=${selectedPartner.id}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (Array.isArray(res.data) && res.data.length > 0) {
                    setPartnerDetail(res.data[0]);
                } else {
                    setPartnerDetail(null);
                }
            } catch (err) {
                console.error('Failed to fetch partner detail:', err);
                setPartnerDetail(null);
            } finally {
                setDetailLoading(false);
            }
        };
        fetchDetail();
    }, [selectedPromotion?.id, selectedPartner?.id]);

    useEffect(() => {
        if (!selectedPromotion?.id) {
            setPartners([]);
            setValue('Partner', null);
            setPartnerDetail(null);
            return;
        }
        const fetchPartners = async () => {
            setPartnersLoading(true);
            try {
                const token = localStorage.getItem('AuthorizationToken');
                const res = await axios.get(
                    `${local_signage}/api/v1/fetchpromotionpartnerlist?PromotionID=${selectedPromotion.id}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (Array.isArray(res.data)) {
                    setPartners(res.data);
                }
            } catch (err) {
                console.error('Failed to fetch partners:', err);
            } finally {
                setPartnersLoading(false);
            }
        };
        fetchPartners();
    }, [selectedPromotion?.id]);

    useEffect(() => {
        const fetchPromotions = async () => {
            setPromotionsLoading(true);
            try {
                const token = localStorage.getItem('AuthorizationToken');
                const res = await axios.get(`${local_signage}/api/v1/fetchpromotion`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.data?.success && Array.isArray(res.data.data)) {
                    setPromotions(res.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch promotions:', err);
                toast.error('Failed to load promotions.');
            } finally {
                setPromotionsLoading(false);
            }
        };
        fetchPromotions();
    }, []);

    const onSubmit = async (data) => {
        setLoading(true);
        setQrDataUrl(null);
        try {
            const token = localStorage.getItem('AuthorizationToken');
            const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

            const promotionId = data.Promotion?.id || "";
            const partnerId = data.Partner?.id || "";

            // Step 1: Try fetching existing detail
            const detailRes = await axios.get(
                `${local_signage}/api/v1/fetchpromotionpartnerdetail?PromotionID=${promotionId}&PartnerID=${partnerId}`,
                { headers }
            );

            let detail = Array.isArray(detailRes.data) && detailRes.data.length > 0 ? detailRes.data[0] : null;

            // Step 2: If no data, insert then fetch again
            if (!detail) {
                const insertRes = await axios.post(
                    `${local_signage}/api/v1/insertpromotionpartnerdetail`,
                    { PromotionId: promotionId, PartnerId: partnerId },
                    { headers }
                );

                if (insertRes.data?.success === false) {
                    toast.error(insertRes.data?.message || 'Failed to insert promotion partner.');
                    return;
                }

                toast.success('Promotion partner saved!');

                const refetchRes = await axios.get(
                    `${local_signage}/api/v1/fetchpromotionpartnerdetail?PromotionID=${promotionId}&PartnerID=${partnerId}`,
                    { headers }
                );
                detail = Array.isArray(refetchRes.data) && refetchRes.data.length > 0 ? refetchRes.data[0] : null;
            }

            if (!detail?.UniqueId) {
                toast.error('No UniqueId found to generate QR.');
                return;
            }

            // Step 3: Generate QR
            const qrUrl = `https://promotions.abispro.com/UID:${detail.UniqueId}`;
            const url = await QRCode.toDataURL(qrUrl, { width: 300, margin: 2 });
            setQrDataUrl(url);
            toast.success('QR Code generated successfully!');
        } catch (err) {
            console.error(err);
            toast.error('Failed to generate QR code.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card sx={{
            borderRadius: '20px',
            boxShadow: '0 12px 40px -12px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.04)',
            overflow: 'visible'
        }}>
            <Box sx={{ p: 4 }}>
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', pb: 2, borderBottom: '1px solid #f0f2f5' }}>
                    <Box sx={{
                        p: 1.5,
                        borderRadius: '12px',
                        backgroundColor: 'rgba(30, 136, 229, 0.1)',
                        color: 'primary.main',
                        mr: 2
                    }}>
                        <LocalOffer />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Promotions Management
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Select and assign promotional schemes to partners.
                        </Typography>
                    </Box>
                </Box>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={3.5}>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="Promotion"
                                control={control}
                                rules={{ required: 'Please select a promotion' }}
                                render={({ field: { onChange, value } }) => (
                                    <Autocomplete
                                        options={promotions.map(p => ({ id: p.PromotionId, label: `${p.PromotionSchemeName} — ${p.PromotionName}` }))}
                                        loading={promotionsLoading}
                                        getOptionLabel={(option) => option.label || ""}
                                        isOptionEqualToValue={(option, val) => option.id === val?.id}
                                        onChange={(_, item) => onChange(item)}
                                        value={value}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Promotion Scheme *"
                                                error={!!errors.Promotion}
                                                helperText={errors.Promotion?.message}
                                                slotProps={{
                                                    input: {
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <React.Fragment>
                                                                {promotionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </React.Fragment>
                                                        ),
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="Partner"
                                control={control}
                                rules={{ required: 'Please select a partner' }}
                                render={({ field: { onChange, value } }) => (
                                    <Autocomplete
                                        options={partners.map(p => ({ id: p.PartnerId, label: `${p.PartnerName} — ${p.Address || ''}` }))}
                                        loading={partnersLoading}
                                        disabled={!selectedPromotion?.id}
                                        getOptionLabel={(option) => option.label || ""}
                                        isOptionEqualToValue={(option, val) => option.id === val?.id}
                                        onChange={(_, item) => onChange(item)}
                                        value={value}
                                        noOptionsText={!selectedPromotion?.id ? 'Select a promotion first' : 'No partners found'}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Partner *"
                                                error={!!errors.Partner}
                                                helperText={errors.Partner?.message || (!selectedPromotion?.id ? 'Select a promotion first' : '')}
                                                slotProps={{
                                                    input: {
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <React.Fragment>
                                                                {partnersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </React.Fragment>
                                                        ),
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>

                        {(detailLoading || partnerDetail) && (
                            <Grid item xs={12}>
                                {detailLoading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading details...</Typography>
                                    </Box>
                                ) : partnerDetail && (
                                    <Box sx={{
                                        p: 2.5,
                                        borderRadius: '10px',
                                        backgroundColor: '#f0f9ff',
                                        border: '1px solid #bae6fd',
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                        gap: 1.5,
                                    }}>
                                        {[
                                            { label: 'Promotion Scheme', value: partnerDetail.PromotionSchemeName },
                                            { label: 'Partner Name', value: partnerDetail.PartnerName },
                                            { label: 'Address', value: partnerDetail.Address },
                                            { label: 'Pin Code', value: partnerDetail.PinCode },
                                            { label: 'Area ID', value: partnerDetail.AreaId },
                                            { label: 'Valid From', value: new Date(partnerDetail.FromDate).toLocaleDateString('en-IN') },
                                            { label: 'Valid To', value: new Date(partnerDetail.ToDate).toLocaleDateString('en-IN') },
                                        ].map(({ label, value }) => (
                                            <Box key={label}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{label}</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{value || '—'}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Box sx={{
                                display: 'flex',
                                gap: 2,
                                justifyContent: 'flex-end',
                                pt: 4,
                                borderTop: '1px solid #f0f2f5',
                                mt: 2
                            }}>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={() => { reset(); setPartnerDetail(null); setQrDataUrl(null); }}
                                    disabled={loading}
                                    sx={{ color: 'text.secondary', borderColor: '#e2e8f0' }}
                                >
                                    Clear
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowRight />}
                                    disabled={loading}
                                    sx={{ minWidth: 160 }}
                                >
                                    {loading ? 'Generating...' : 'Create QR'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>

                {qrDataUrl && (
                    <Box sx={{
                        mt: 4,
                        pt: 4,
                        borderTop: '1px solid #f0f2f5',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                    }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Generated QR Code
                        </Typography>
                        <Box sx={{
                            p: 2,
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.06)'
                        }}>
                            <img src={qrDataUrl} alt="QR Code" style={{ display: 'block', width: 240, height: 240 }} />
                        </Box>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                const a = document.createElement('a');
                                a.href = qrDataUrl;
                                a.download = `QR_${partnerDetail?.UniqueId || 'code'}.png`;
                                a.click();
                            }}
                        >
                            Download QR
                        </Button>
                    </Box>
                )}
            </Box>
        </Card>
    );
};

const PromotionDetailReport = () => {
    const [promotions, setPromotions] = useState([]);
    const [promotionsLoading, setPromotionsLoading] = useState(false);
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [selectedPromotionId, setSelectedPromotionId] = useState('');
    const [selectedPartnerId, setSelectedPartnerId] = useState('All');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activePartner, setActivePartner] = useState('All');
    const [exportLoading, setExportLoading] = useState(false);

    const token = localStorage.getItem('AuthorizationToken');

    const formatDate = (str) => {
        if (!str) return '-';
        const m = str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
        if (!m) return str;
        return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
    };

    useEffect(() => {
        const fetchPromotions = async () => {
            setPromotionsLoading(true);
            try {
                const res = await axios.get(`${local_signage}/api/v1/fetchpromotion`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.data?.success && Array.isArray(res.data.data)) {
                    setPromotions(res.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch promotions:', err);
            } finally {
                setPromotionsLoading(false);
            }
        };
        fetchPromotions();
    }, []);

    useEffect(() => {
        if (!selectedPromotionId) { setPartners([]); return; }
        const fetchPartners = async () => {
            setPartnersLoading(true);
            try {
                const res = await axios.get(
                    `${local_signage}/api/v1/fetchpromotionpartnerlist?PromotionID=${selectedPromotionId}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (Array.isArray(res.data)) setPartners(res.data);
            } catch (err) {
                console.error('Failed to fetch partners:', err);
            } finally {
                setPartnersLoading(false);
            }
        };
        fetchPartners();
    }, [selectedPromotionId]);

    const handleFetch = async () => {
        if (!selectedPromotionId) { setError('Please select a promotion.'); return; }
        setLoading(true);
        setError('');
        setReportData([]);
        setActivePartner('All');
        try {
            const partnerParam = selectedPartnerId === 'All' ? 'All' : selectedPartnerId;
            const res = await axios.get(
                `${local_signage}/api/v1/reportjsonpromotiondetail?PromotionId=${selectedPromotionId}&PartnerId=${partnerParam}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const list = res.data?.promotions || [];
            setReportData(list);
            if (list.length === 0) setError('No data found for the selected filters.');
        } catch (err) {
            setError('Failed to fetch report data.');
        } finally {
            setLoading(false);
        }
    };

    const allCustomers = reportData.flatMap(p =>
        (p.customers || []).map(c => ({ ...c, PartnerName: p.PartnerName, PartnerId: p.PartnerId }))
    );

    const displayCustomers = activePartner === 'All'
        ? allCustomers
        : (reportData.find(p => p.PartnerId === activePartner)?.customers || []).map(c => ({
            ...c,
            PartnerName: reportData.find(p => p.PartnerId === activePartner)?.PartnerName || ''
        }));

    const exportToExcel = () => {
        if (displayCustomers.length === 0) return;
        setExportLoading(true);
        try {
            const wb = XLSX.utils.book_new();
            const headers = ['Sr.No', 'Customer ID', 'Customer Name', 'Mobile', 'Email', 'Promo Code', 'Q1 Answer', 'Created Date', 'Partner Name'];
            const rows = displayCustomers.map((c, i) => [
                i + 1,
                c.Id || '-',
                c.CustomerName || '-',
                c.Mobile || '-',
                c.Email || '-',
                c.PromoCode || '-',
                c.Q1Ans || '-',
                formatDate(c.CreatedDate),
                c.PartnerName || '-'
            ]);
            const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            ws['!cols'] = [{ wch: 8 }, { wch: 14 }, { wch: 20 }, { wch: 14 }, { wch: 26 }, { wch: 14 }, { wch: 10 }, { wch: 20 }, { wch: 22 }];
            XLSX.utils.book_append_sheet(wb, ws, 'Promotion Report');
            const partnerLabel = activePartner === 'All' ? 'All' : (reportData.find(p => p.PartnerId === activePartner)?.PartnerName || activePartner);
            XLSX.writeFile(wb, `PromotionReport_${selectedPromotionId}_${partnerLabel}.xlsx`);
        } catch (err) {
            setError('Export failed.');
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <Card sx={{ borderRadius: '20px', boxShadow: '0 12px 40px -12px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.04)' }}>
            <Box sx={{ p: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', pb: 2, borderBottom: '1px solid #f0f2f5' }}>
                    <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: 'rgba(30,136,229,0.1)', color: 'primary.main', mr: 2 }}>
                        <Assessment />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>Promotion Detail Report</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>View registered customers per partner for any promotion.</Typography>
                    </Box>
                </Box>

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
                    <TextField
                        select
                        label="Promotion Scheme"
                        value={selectedPromotionId}
                        onChange={e => { setSelectedPromotionId(e.target.value); setSelectedPartnerId('All'); setReportData([]); }}
                        disabled={promotionsLoading}
                        sx={{ minWidth: 240 }}
                        size="small"
                    >
                        {promotionsLoading
                            ? <MenuItem disabled value="">Loading...</MenuItem>
                            : promotions.map(p => (
                                <MenuItem key={p.PromotionId} value={p.PromotionId}>
                                    {p.PromotionSchemeName} — {p.PromotionName}
                                </MenuItem>
                            ))
                        }
                    </TextField>

                    <TextField
                        select
                        label="Partner"
                        value={selectedPartnerId}
                        onChange={e => setSelectedPartnerId(e.target.value)}
                        disabled={partnersLoading || !selectedPromotionId}
                        sx={{ minWidth: 200 }}
                        size="small"
                    >
                        <MenuItem value="All">All Partners</MenuItem>
                        {partnersLoading
                            ? <MenuItem disabled value="">Loading...</MenuItem>
                            : partners.map(p => (
                                <MenuItem key={p.PartnerId} value={p.PartnerId}>{p.PartnerName}</MenuItem>
                            ))
                        }
                    </TextField>

                    <Button
                        variant="contained"
                        onClick={handleFetch}
                        disabled={loading || !selectedPromotionId}
                        endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
                        sx={{ height: 40 }}
                    >
                        {loading ? 'Loading...' : 'Fetch Report'}
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={exportToExcel}
                        disabled={exportLoading || displayCustomers.length === 0}
                        sx={{ height: 40 }}
                    >
                        {exportLoading ? 'Exporting...' : 'Export Excel'}
                    </Button>

                    {reportData.length > 0 && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', ml: 'auto' }}>
                            {displayCustomers.length} customer{displayCustomers.length !== 1 ? 's' : ''} • {reportData.length} partner{reportData.length !== 1 ? 's' : ''}
                        </Typography>
                    )}
                </Box>

                {error && (
                    <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', backgroundColor: '#fff3f3', border: '1px solid #fca5a5' }}>
                        <Typography variant="body2" sx={{ color: '#dc2626' }}>{error}</Typography>
                    </Box>
                )}

                {/* Partner Pill Buttons */}
                {reportData.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Button
                            size="small"
                            onClick={() => setActivePartner('All')}
                            sx={{
                                borderRadius: '50px',
                                px: 2,
                                py: 0.5,
                                fontSize: '13px',
                                fontWeight: activePartner === 'All' ? 700 : 500,
                                backgroundColor: activePartner === 'All' ? 'primary.main' : '#e2e8f0',
                                color: activePartner === 'All' ? '#fff' : 'text.secondary',
                                '&:hover': { backgroundColor: activePartner === 'All' ? 'primary.dark' : '#cbd5e1' }
                            }}
                        >
                            All ({allCustomers.length})
                        </Button>
                        {reportData.map(p => (
                            <Button
                                key={p.PartnerId}
                                size="small"
                                onClick={() => setActivePartner(p.PartnerId)}
                                sx={{
                                    borderRadius: '50px',
                                    px: 2,
                                    py: 0.5,
                                    fontSize: '13px',
                                    fontWeight: activePartner === p.PartnerId ? 700 : 500,
                                    backgroundColor: activePartner === p.PartnerId ? 'primary.main' : '#e2e8f0',
                                    color: activePartner === p.PartnerId ? '#fff' : 'text.secondary',
                                    '&:hover': { backgroundColor: activePartner === p.PartnerId ? 'primary.dark' : '#cbd5e1' }
                                }}
                            >
                                {p.PartnerName} ({(p.customers || []).length})
                            </Button>
                        ))}
                    </Box>
                )}

                {/* Active Partner Address */}
                {activePartner !== 'All' && (() => {
                    const p = reportData.find(x => x.PartnerId === activePartner);
                    return p ? (
                        <Box sx={{ mb: 2, px: 2, py: 1, borderRadius: '8px', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', display: 'inline-flex', gap: 3 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                <strong>Partner ID:</strong> {p.PartnerId}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                <strong>Address:</strong> {p.Address || '—'}
                            </Typography>
                        </Box>
                    ) : null;
                })()}

                {/* Customer Table */}
                {reportData.length > 0 && (
                    <Box sx={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#1e88e5', color: '#fff' }}>
                                    {['Sr.No', 'Customer ID', 'Customer Name', 'Mobile', 'Email', 'Promo Code', 'Q1 Ans', 'Created Date', ...(activePartner === 'All' ? ['Partner'] : [])].map(h => (
                                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 600 }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {displayCustomers.length === 0 ? (
                                    <tr><td colSpan={activePartner === 'All' ? 9 : 8} style={{ padding: '20px', textAlign: 'center', color: '#718096' }}>No customers found.</td></tr>
                                ) : displayCustomers.map((c, i) => (
                                    <tr key={c.Id + i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f2f5' }}>{i + 1}</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f2f5', fontFamily: 'monospace' }}>{c.Id || '-'}</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f2f5', fontWeight: 500 }}>{c.CustomerName || '-'}</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f2f5' }}>{c.Mobile || '-'}</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f2f5' }}>{c.Email || '-'}</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f2f5', fontFamily: 'monospace', color: '#1e88e5', fontWeight: 600 }}>{c.PromoCode || '-'}</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f2f5', textAlign: 'center' }}>
                                            <span style={{ padding: '2px 10px', borderRadius: '12px', backgroundColor: c.Q1Ans === 'Y' ? '#dcfce7' : '#fee2e2', color: c.Q1Ans === 'Y' ? '#16a34a' : '#dc2626', fontSize: '12px', fontWeight: 600 }}>
                                                {c.Q1Ans || '-'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }}>{formatDate(c.CreatedDate)}</td>
                                        {activePartner === 'All' && (
                                            <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f2f5' }}>{c.PartnerName || '-'}</td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                )}

                {!loading && reportData.length === 0 && !error && (
                    <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                        <Assessment sx={{ fontSize: 60, color: '#e2e8f0', mb: 1 }} />
                        <Typography variant="body1">Select a promotion and click "Fetch Report" to view data.</Typography>
                    </Box>
                )}
            </Box>
        </Card>
    );
};

const PartnerQRListTab = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const searchRef = useRef(null);
    const token = localStorage.getItem('AuthorizationToken');

    const fetchAndGenerate = async () => {
        setLoading(true);
        setError('');
        setRows([]);
        try {
            const res = await axios.get(
                `${local_signage}/api/v1/getjsonpromotionpartnerlist?AreaId=ALL&PinCode=ALL&PartnerName=ALL&PromotionID=ALL&PromotionSchemeId=ALL`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const partners = res.data?.PromoPartner || [];
            const flat = [];
            for (const p of partners) {
                for (const pd of (p.promodetails || [])) {
                    const qrThumb = await QRCode.toDataURL(
                        `https://promotions.abispro.com/UID:${pd.UniqueId}`,
                        { width: 100, margin: 1 }
                    );
                    flat.push({
                        partnerId: p.PartnerId,
                        partnerName: p.PartnerName,
                        address: p.Address || '—',
                        areaId: p.AreaId || '—',
                        uniqueId: pd.UniqueId,
                        promotionId: pd.PromotionId,
                        promotionName: pd.PromotionName || pd.PromotionId,
                        qrUrl: qrThumb,
                    });
                }
            }
            setRows(flat);
        } catch (err) {
            setError('Failed to load partner QR list.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAndGenerate(); }, []);

    const q = search.toLowerCase();
    const filtered = q
        ? rows.filter(r =>
            r.partnerName.toLowerCase().includes(q) ||
            r.areaId.toLowerCase().includes(q) ||
            r.promotionId.toLowerCase().includes(q) ||
            r.promotionName.toLowerCase().includes(q) ||
            r.partnerId.toLowerCase().includes(q)
        )
        : rows;

    const downloadQR = async (row) => {
        const qrValue = `https://promotions.abispro.com/UID:${row.uniqueId}`;
        const url = await QRCode.toDataURL(qrValue, { width: 400, margin: 2 });
        const a = document.createElement('a');
        a.href = url;
        a.download = `QR_${row.partnerName.replace(/\s+/g, '_')}_${row.promotionName.replace(/\s+/g, '_')}.png`;
        a.click();
    };

    const downloadAll = () => filtered.forEach(r => downloadQR(r));

    const tdBase = { borderBottom: '1px solid #f0f4f8', verticalAlign: 'middle', padding: '9px 12px', fontSize: '13px' };

    return (
        <Card sx={{ borderRadius: '20px', boxShadow: '0 12px 40px -12px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.04)' }}>
            <Box sx={{ p: 3 }}>
                {/* Header row */}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', pb: 2, borderBottom: '1px solid #f0f2f5', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                        <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: 'rgba(30,136,229,0.1)', color: 'primary.main' }}>
                            <CardGiftcard />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>List Partner QR</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {rows.length > 0
                                    ? `${[...new Set(rows.map(r => r.partnerId))].length} partners · ${rows.length} QR codes`
                                    : 'All partner QR codes'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                            ref={searchRef}
                            type="text"
                            placeholder="Search by name / area / promo ID..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{
                                padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: '8px',
                                fontSize: '13px', width: '240px', outline: 'none', backgroundColor: '#fafbfc',
                                fontFamily: 'inherit'
                            }}
                        />
                        {search && (
                            <button
                                onClick={() => { setSearch(''); searchRef.current?.focus(); }}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: '#718096', fontSize: '16px', lineHeight: 1, padding: '0 4px'
                                }}
                            >✕</button>
                        )}
                        <Button size="small" variant="outlined" onClick={fetchAndGenerate} disabled={loading} sx={{ height: 34, minWidth: 80 }}>
                            {loading ? <CircularProgress size={13} /> : 'Refresh'}
                        </Button>
                        <Button size="small" variant="contained" onClick={downloadAll} disabled={filtered.length === 0} sx={{ height: 34 }}>
                            Download All ({filtered.length})
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', backgroundColor: '#fff3f3', border: '1px solid #fca5a5' }}>
                        <Typography variant="body2" sx={{ color: '#dc2626' }}>{error}</Typography>
                    </Box>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                        <CircularProgress size={36} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Generating QR codes, please wait...</Typography>
                    </Box>
                ) : (
                    <Box sx={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#1e88e5', position: 'sticky', top: 0, zIndex: 1 }}>
                                    {['#', 'Partner Name', 'Area ID', 'Address', 'Promotion ID', 'QR Code', ''].map((h, i) => (
                                        <th key={i} style={{
                                            padding: '10px 12px', textAlign: 'left', color: '#fff',
                                            fontWeight: 600, whiteSpace: 'nowrap', fontSize: '12px'
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
                                            {rows.length === 0 ? 'No data loaded.' : 'No results match your search.'}
                                        </td>
                                    </tr>
                                ) : filtered.map((row, idx) => {
                                    const prevPartnerId = idx > 0 ? filtered[idx - 1].partnerId : null;
                                    const isNewPartner = row.partnerId !== prevPartnerId;
                                    const rowBg = idx % 2 === 0 ? '#fff' : '#f8fafc';
                                    return (
                                        <tr key={row.uniqueId} style={{
                                            backgroundColor: rowBg,
                                            borderTop: isNewPartner && idx > 0 ? '2px solid #e2e8f0' : undefined
                                        }}>
                                            <td style={{ ...tdBase, color: '#a0aec0', fontWeight: 500, minWidth: 36 }}>{idx + 1}</td>
                                            <td style={{ ...tdBase, fontWeight: isNewPartner ? 600 : 400, color: isNewPartner ? '#2d3748' : '#718096', minWidth: 150 }}>
                                                {isNewPartner ? (
                                                    <>
                                                        <div style={{ fontWeight: 600, color: '#2d3748' }}>{row.partnerName}</div>
                                                        <div style={{ fontSize: '11px', color: '#a0aec0', marginTop: '1px' }}>{row.partnerId}</div>
                                                    </>
                                                ) : (
                                                    <span style={{ color: '#a0aec0', fontSize: '12px', paddingLeft: '8px' }}>↳ {row.partnerName}</span>
                                                )}
                                            </td>
                                            <td style={{ ...tdBase, color: '#4a5568', minWidth: 80 }}>{row.areaId}</td>
                                            <td style={{ ...tdBase, color: '#718096', maxWidth: 180, wordBreak: 'break-word' }}>{row.address}</td>
                                            <td style={{ ...tdBase, minWidth: 130 }}>
                                                <div style={{ fontWeight: 500, color: '#2d3748', fontSize: '13px' }}>{row.promotionName}</div>
                                                <div style={{ fontSize: '11px', color: '#a0aec0', fontFamily: 'monospace', marginTop: '1px' }}>{row.promotionId}</div>
                                            </td>
                                            <td style={{ ...tdBase, padding: '6px 12px' }}>
                                                <img
                                                    src={row.qrUrl}
                                                    alt="QR"
                                                    style={{ width: 60, height: 60, display: 'block', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                                />
                                            </td>
                                            <td style={{ ...tdBase, minWidth: 100 }}>
                                                <button
                                                    onClick={() => downloadQR(row)}
                                                    style={{
                                                        padding: '5px 12px', border: 'none', borderRadius: '6px',
                                                        backgroundColor: '#1e88e5', color: '#fff', cursor: 'pointer',
                                                        fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    ↓ Download
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Box>
                )}
            </Box>
        </Card>
    );
};

const ScanReport = () => {
    const token = localStorage.getItem('AuthorizationToken');

    const [areaOptions, setAreaOptions] = useState([]);
    const [selectedArea, setSelectedArea] = useState('ALL');
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [trackerData, setTrackerData] = useState([]);
    const [customerListData, setCustomerListData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [exportLoading, setExportLoading] = useState(false);

    useEffect(() => {
        axios.get(`${local_url}/api/SFArea/0010003871`, {
            headers: { 'Authorization': `Bearer ${token}` }
        }).then(res => {
            if (Array.isArray(res.data))
                setAreaOptions(res.data.map(a => ({ id: a.sfAreaId, label: a.sfAreaName })));
        }).catch(() => { });

        fetchReport();
    }, []);

    const fetchReport = async (sd, ed, area) => {
        const useSd = sd ?? startDate;
        const useEd = ed ?? endDate;
        const useArea = area ?? selectedArea;
        setLoading(true);
        setError('');
        setTrackerData([]);
        setCustomerListData([]);
        try {
            const res = await axios.get(
                `${local_signage}/api/v1/reportjsonpromotionqrscan?AreaId=${useArea}&StartDate=${useSd.replace(/-/g, '')}&EndDate=${useEd.replace(/-/g, '')}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const qrScans = res.data?.QRScan || [];

            // Group by PartnerName — same store can appear multiple times across dates
            const storeMap = new Map();
            for (const scan of qrScans) {
                const key = scan.PartnerName;
                if (!storeMap.has(key)) {
                    storeMap.set(key, { storeName: scan.PartnerName, scanCountSum: 0, customers: new Map() });
                }
                const store = storeMap.get(key);
                store.scanCountSum += (Number(scan.ScanCount) || 0);
                for (const c of (scan.couponGen || [])) {
                    if (!store.customers.has(c.Id)) {
                        store.customers.set(c.Id, {
                            customerName: c.CustomerName,
                            mobile: c.Mobile,
                            // No redeem record = coupon was used/consumed; has redeem (TimesUsed:0) = still pending
                            availed: !(Array.isArray(c.redeem) && c.redeem.length > 0),
                        });
                    }
                }
            }

            const tracker = [];
            const customerList = [];
            let totalQrScans = 0, totalCoupons = 0, totalAvailed = 0;

            for (const [, store] of storeMap) {
                const coupons = Array.from(store.customers.values());
                const qrScanCount = store.scanCountSum;
                const couponCount = coupons.length;
                const availed = coupons.filter(c => c.availed).length;
                const unused = couponCount - availed;
                totalQrScans += qrScanCount;
                totalCoupons += couponCount;
                totalAvailed += availed;
                tracker.push({ storeName: store.storeName, qrScanCount, couponCount, availed, unused });
                coupons.forEach((c) => {
                    customerList.push({
                        storeName: store.storeName,
                        customerName: c.customerName,
                        mobile: c.mobile,
                    });
                });
            }

            tracker.push({
                storeName: 'Total',
                qrScanCount: totalQrScans,
                couponCount: totalCoupons,
                availed: totalAvailed,
                unused: totalCoupons - totalAvailed,
                isTotal: true
            });
            setTrackerData(tracker);
            setCustomerListData(customerList);
            if (tracker.length <= 1) setError('No scan data found for the selected filters.');
        } catch {
            setError('Failed to fetch scan report.');
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = () => {
        if (!trackerData.length) return;
        setExportLoading(true);
        try {
            const wb = XLSX.utils.book_new();
            const ws1 = XLSX.utils.aoa_to_sheet([
                ['GT Store Name', 'No. of QR Scans', 'No. of Coupon Generated', 'Used Coupon', 'Unused Coupon'],
                ...trackerData.map(r => [r.storeName, r.qrScanCount, r.couponCount, r.availed, r.unused]),
            ]);
            ws1['!cols'] = [{ wch: 28 }, { wch: 18 }, { wch: 24 }, { wch: 16 }, { wch: 16 }];
            XLSX.utils.book_append_sheet(wb, ws1, 'Daily Tracker');
            const ws2 = XLSX.utils.aoa_to_sheet([
                ['GT Store Name', 'Customer Mob No.'],
                ...customerListData.map(r => [r.storeName, r.mobile]),
            ]);
            ws2['!cols'] = [{ wch: 28 }, { wch: 16 }];
            XLSX.utils.book_append_sheet(wb, ws2, 'Customer List');
            XLSX.writeFile(wb, `ScanReport_${startDate}_to_${endDate}.xlsx`);
        } catch { setError('Export failed.'); }
        finally { setExportLoading(false); }
    };

    const th = { padding: '10px 12px', color: '#fff', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap', fontSize: '12px' };
    const td = { padding: '8px 12px', borderBottom: '1px solid #f0f2f5', fontSize: '13px' };
    const hasData = trackerData.length > 1;

    return (
        <Card sx={{ borderRadius: '20px', boxShadow: '0 12px 40px -12px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.04)' }}>
            <Box sx={{ p: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', pb: 2, borderBottom: '1px solid #f0f2f5' }}>
                    <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: 'rgba(30,136,229,0.1)', color: 'primary.main', mr: 2 }}>
                        <CropFree />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Scan Report</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>QR code scans by customers across promotions and partners.</Typography>
                    </Box>
                </Box>

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
                    <TextField select label="Area" value={selectedArea} onChange={e => setSelectedArea(e.target.value)} sx={{ minWidth: 180 }} size="small">
                        <MenuItem value="ALL">All Areas</MenuItem>
                        {areaOptions.map(a => <MenuItem key={a.id} value={a.id}>{a.label}</MenuItem>)}
                    </TextField>
                    <TextField type="date" label="Start Date" value={startDate} onChange={e => setStartDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 155 }} />
                    <TextField type="date" label="End Date" value={endDate} onChange={e => setEndDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 155 }} />
                    <Button variant="contained" onClick={() => fetchReport()} disabled={loading || !startDate || !endDate} endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />} sx={{ height: 40 }}>
                        {loading ? 'Loading...' : 'Fetch'}
                    </Button>
                    <Button variant="outlined" onClick={exportToExcel} disabled={exportLoading || !hasData} sx={{ height: 40 }}>
                        {exportLoading ? 'Exporting...' : 'Export Excel'}
                    </Button>
                </Box>

                {error && (
                    <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', backgroundColor: '#fff3f3', border: '1px solid #fca5a5' }}>
                        <Typography variant="body2" sx={{ color: '#dc2626' }}>{error}</Typography>
                    </Box>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Fetching scan data...</Typography>
                    </Box>
                ) : hasData ? (
                    <>
                        {/* Daily Tracker */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Daily Tracker</Typography>
                        <Box sx={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #e2e8f0', mb: 4 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#1e88e5' }}>
                                        {['GT Store Name', 'No. of QR Scans', 'No. of Coupon Generated', 'Used Coupon', 'Unused Coupon'].map(h => (
                                            <th key={h} style={th}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {trackerData.map((r, i) => (
                                        <tr key={i} style={{ backgroundColor: r.isTotal ? '#e3f2fd' : i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                            <td style={{ ...td, fontWeight: r.isTotal ? 700 : 500 }}>{r.storeName}</td>
                                            <td style={{ ...td, textAlign: 'center', fontWeight: r.isTotal ? 700 : 400 }}>{r.qrScanCount}</td>
                                            <td style={{ ...td, textAlign: 'center', fontWeight: r.isTotal ? 700 : 400 }}>{r.couponCount}</td>
                                            <td style={{ ...td, textAlign: 'center', fontWeight: r.isTotal ? 700 : 400, color: r.availed > 0 ? '#2e7d32' : 'inherit' }}>{r.availed}</td>
                                            <td style={{ ...td, textAlign: 'center', fontWeight: r.isTotal ? 700 : 400, color: r.unused > 0 ? '#c62828' : 'inherit' }}>{r.unused}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>

                        {/* Customer List */}
                        {customerListData.length > 0 && (
                            <>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Customer List</Typography>
                                <Box sx={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#43a047' }}>
                                                {['GT Store Name', 'Customer Mob No.'].map(h => (
                                                    <th key={h} style={th}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {customerListData.map((r, i) => (
                                                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                    <td style={{ ...td, fontWeight: r.storeName ? 600 : 400, color: r.storeName ? '#1a202c' : '#cbd5e0' }}>
                                                        {r.storeName || '—'}
                                                    </td>
                                                    <td style={{ ...td, fontFamily: 'monospace' }}>{r.mobile || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Box>
                            </>
                        )}
                    </>
                ) : !error && (
                    <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                        <CropFree sx={{ fontSize: 60, color: '#e2e8f0', mb: 1 }} />
                        <Typography variant="body1">Showing results for current month. Adjust filters and click "Fetch" to reload.</Typography>
                    </Box>
                )}
            </Box>
        </Card>
    );
};

const PlaceholderTab = ({ title, description, icon }) => (
    <Card sx={{
        borderRadius: '20px',
        minHeight: 450,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed #cbd5e1',
        backgroundColor: 'rgba(255,255,255,0.5)',
        boxShadow: 'none'
    }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400, p: 3 }}>
            <Box sx={{ mb: 3 }}>{icon}</Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                {title}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
                {description}
            </Typography>
            <Button variant="contained" disabled sx={{ borderRadius: '50px' }}>
                Coming Soon
            </Button>
        </Box>
    </Card>
);

export default AdminQRCoupon;